import React from "react";
import { Star, Quote } from "lucide-react";
import { SITE, REVIEW_DISTRIBUTION, REVIEW_TAGS } from "@/lib/site-data";

const GOOGLE_REVIEWS = [
  {
    name: "Felipe Da Silva",
    when: "2 meses atrás",
    rating: 5,
    text: "O atendimento do Bruno é impecável. Saio renovado a cada sessão — kkkk. Profissionalismo do início ao fim.",
  },
  {
    name: "Erly Penha de Almeida",
    when: "1 ano atrás",
    rating: 5,
    text: "Conheci Sr. Osvaldo e Janaína. Após 10 sessões não precisei mais de medicamento. Mudou minha vida.",
  },
  {
    name: "Robson Eloi",
    when: "1 ano atrás",
    rating: 5,
    text: "Cheguei com dor severa na coluna. Após 10 sessões voltei à minha rotina e aos esportes. Recomendo!",
  },
  {
    name: "Marina S.",
    when: "8 meses atrás",
    rating: 5,
    text: "Super recomendo. Profissionais atenciosos e ambiente acolhedor. Saí da primeira sessão renovada.",
  },
  {
    name: "Carla R.",
    when: "5 meses atrás",
    rating: 5,
    text: "Janaína fora do normal! Equipe nota 1000. Atendimento humano que faz toda a diferença.",
  },
  {
    name: "Paulo F.",
    when: "1 ano atrás",
    rating: 5,
    text: "Escapei de uma cirurgia na coluna. Tratamento sério, baseado em evidência e muito carinho.",
  },
];

export default function ReviewsStats() {
  const total = REVIEW_DISTRIBUTION.reduce((s, r) => s + r.count, 0) || 1;

  return (
    <section id="avaliacoes" className="py-20 md:py-28 bg-white" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Avaliações Google</p>
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-stone-900 max-w-3xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Pacientes reais. <span className="text-orange-600">Resultados reais.</span>
        </h2>

        <div className="mt-12 grid lg:grid-cols-5 gap-8 lg:gap-10">
          {/* Score Card */}
          <div className="lg:col-span-2 rounded-3xl bg-gradient-to-br from-orange-50 via-orange-50/70 to-white border border-orange-100 p-8 md:p-10 text-center self-start">
            <div className="text-7xl md:text-8xl font-extrabold text-stone-900 leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {SITE.rating.toString().replace(".", ",")}
            </div>
            <div className="mt-3 flex items-center justify-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-yellow-500 text-yellow-500" />
              ))}
            </div>
            <div className="mt-3 text-stone-600">{SITE.reviewsCount} avaliações no Google</div>
            <div className="mt-6 h-px bg-orange-100" />
            <p className="mt-6 text-sm text-stone-600">
              "Atendimento humano e técnico. <strong>Mais de 90% dos pacientes recomendam.</strong>"
            </p>
          </div>

          {/* Distribution + Tags */}
          <div className="lg:col-span-3 space-y-7">
            <div className="rounded-3xl bg-stone-50 border border-stone-100 p-7">
              <h3 className="font-semibold text-stone-900 mb-5">Distribuição das avaliações</h3>
              <div className="space-y-3">
                {REVIEW_DISTRIBUTION.map((row) => {
                  const pct = (row.count / total) * 100;
                  return (
                    <div key={row.stars} className="flex items-center gap-4" data-testid={`star-row-${row.stars}`}>
                      <div className="w-14 flex items-center gap-1 text-sm font-semibold text-stone-700">
                        {row.stars} <Star className="w-3.5 h-3.5 fill-yellow-500 text-yellow-500" />
                      </div>
                      <div className="flex-1 h-2.5 rounded-full bg-stone-200 overflow-hidden">
                        <div className="h-full bg-orange-500 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="w-10 text-right text-sm font-semibold text-stone-700">{row.count}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-stone-900 mb-3">Mencionado pelos pacientes</h3>
              <div className="flex flex-wrap gap-2.5">
                {REVIEW_TAGS.map((t) => (
                  <span key={t} className="px-4 py-2 rounded-full bg-white border border-stone-200 text-sm text-stone-700 hover:border-orange-600 hover:text-orange-600 transition-colors cursor-default">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Google review cards */}
        <div className="mt-14">
          <h3 className="font-semibold text-stone-900 mb-5">O que dizem no Google</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {GOOGLE_REVIEWS.map((r, i) => (
              <article key={i} data-testid={`google-review-${i}`} className="rounded-2xl bg-stone-50 border border-stone-100 p-6 hover:border-orange-200 transition-colors">
                <Quote className="w-6 h-6 text-orange-500/70" />
                <p className="mt-3 text-stone-700 text-sm leading-relaxed">{r.text}</p>
                <div className="mt-5 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-stone-900 text-sm">{r.name}</div>
                    <div className="text-xs text-stone-500">{r.when}</div>
                  </div>
                  <div className="flex gap-0.5">
                    {[...Array(r.rating)].map((_, k) => <Star key={k} className="w-3.5 h-3.5 fill-yellow-500 text-yellow-500" />)}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
